import Handlebars from 'handlebars';
import Widget from '../modules/Widget';
import { BASE_IMG_PATH, NEXT_BEST_MATCH_SCORE } from '../consts';
import EventEmitter from '../modules/EventEmitter';
import { formatCurrency } from '@yesplz/core';

const { document } = window;

const nothingFoundTemplate = `
<h2>Nothing found!</h2>
`;

const customItemTemplate = `<div><h3>Custom Item</h3></div>`;

const itemTemplate = `
<a href="{{srcUrl}}" target="{{linkTarget}}" class="ProductGrid">
  <div class="ProductGrid-thumbnail">
    <img src="{{frontImg}}" alt="{{brand}}" />
  </div>
  <div class="ProductGrid-detail">
    <h5>{{name}}</h5>
    {{{priceHtml}}}
  </div>
  {{#if customFields.likes}}
    <span>{{customFields.likes}}</span>
  {{/if}}
</a>
`;

const itemPriceTemplate = `<div class="ProductGrid-price">{{price}}</div>`;
const itemDiscountPriceTemplate = `
<div class="ProductGrid-price sale">{{price}}</div>
<div class="ProductGrid-originalPrice">{{originalPrice}}</div>
`;

const topMatchesTitleTemplate = `
<li class="text-item">
  <h2>
    Top search results
  </h2>
</li>
`;

const nextBestMatchingTitleTemplate = `
<li class="text-item">
  <h2>
    {{#if count}}
      How about the next best matches?
    {{else}}
      We couldn’t find the exact matches, but these items might interest you.
    {{/if}}
  </h2>
</li>
`;

const loadingBarTemplate = `
  <div id="loading-bar" class="loading-mask" data-role="loader" style="display: none;"><div class="loader"><img alt="Loading..." src="https://us.wconcept.com/static/version1661520181/frontend/Corra/wconcept/en_US/images/loader-2.gif"><p>Please wait...</p></div></div>
`

class ProductsList extends Widget {
  defaultParams = {
    imgBaseURL: BASE_IMG_PATH,
    containerClassName: 'ProductList-wrapper',
    rootTag: 'div',
    scrollContainer: window,
    productUrl: null,
    productLinkTarget: '_self',
    /**
     * Available options are:
     * - perRow: @number
     * - className: @string
     */
    row: false,
    displayNextBestMatchesTitle: true,
    templates: {
      customItem: customItemTemplate,
      item: itemTemplate,
      price: itemPriceTemplate,
      discountPrice: itemDiscountPriceTemplate,
      nextBestMatchingTitle: nextBestMatchingTitleTemplate,
      nothingFound: nothingFoundTemplate,
    },
    onRendered: null,
    modifyItemsBeforeRender: null,
    currency: {},
  };

  constructor(params) {
    super(params);

    const element = document.createElement(this.params.rootTag);
    element.className = this.params.containerClassName;
    this.mainElement = element;
  }

  didMount() {
    this.customItemTemplate = Handlebars.compile(this.params.templates.customItem);
    this.itemTemplate = Handlebars.compile(this.params.templates.item);
    this.itemPriceTemplate = Handlebars.compile(this.params.templates.price);
    this.itemDiscountPriceTemplate = Handlebars.compile(
      this.params.templates.discountPrice
    );
    this.nextBestMatchingTitleTemplate = Handlebars.compile(
      nextBestMatchingTitleTemplate,
      // this.params.templates.nextBestMatchingTitle
    );
    this.topMatchesTitleTemplate = Handlebars.compile(
      topMatchesTitleTemplate,
      // this.params.templates.topMatchesTitle
    );
    this.nothingFoundTemplate = Handlebars.compile(this.params.templates.nothingFound);

    this.scrollContainer = (
      typeof this.params.scrollContainer === 'string'
        ? document.querySelector(this.params.scrollContainer)
        : this.params.scrollContainer
    );

    this.search = this.main.store.getState().search;
    this.main.store.subscribe(() => {
      this.search = this.main.store.getState().search;
      this.update();
    });
  }

  didUpdate(prevState) {
    if (!prevState.search.isLoading && this.state.search.isLoading) {
      this.showLoadingBar();
    }
    else if (prevState.search.isLoading && !this.state.search.isLoading) {
      this.hideLoadingBar();
    }
  }

  createLoadingBar() {
    this.container.insertAdjacentHTML('afterbegin', loadingBarTemplate);
      this.loadingBar = this.container.querySelector('#loading-bar');
  }

  showLoadingBar() {
    if (!this.loadingBar) {
      this.createLoadingBar();
    }
    this.loadingBar.style.display = 'block';
  }

  hideLoadingBar() {
    if (!this.loadingBar) {
      this.createLoadingBar();
    }
    this.loadingBar.style.display = 'none';
  }

  formatCurrency(number) {
    return formatCurrency(number,  this.params.currency);
  }

  getProductUrl(product) {
    if (typeof this.params.productUrl === 'function') {
      return this.params.productUrl(product);
    }
    return product.productUrl;
  }

  renderCustomItem(item) {
    const element = document.createElement('div');

    element.innerHTML = this.customItemTemplate(item);

    return element.firstElementChild;
  }

  renderItem(item) {
    const element = document.createElement('div');

    const discountValue = (
      item.salePrice !== item.originalPrice
        ? 100 - Math.ceil((item.salePrice / item.originalPrice) * 100)
        : ''
    );

    const priceHtml = (
      item.originalPrice && item.salePrice !== item.originalPrice
        ? this.itemDiscountPriceTemplate({
            price: this.formatCurrency(item.salePrice),
            originalPrice: this.formatCurrency(item.originalPrice),
            discountValue,
          })
        : item.salePrice
          ? this.itemPriceTemplate({ price: this.formatCurrency(item.salePrice) })
          : ''
    );

    element.innerHTML = this.itemTemplate({
      ...item,
      srcUrl: this.getProductUrl(item),
      linkTarget: this.params.productLinkTarget,
      frontImg: (
        item.frontImgSrc
          ? item.frontImgSrc
          : item.frontImg
            ? `${this.params.imgBaseURL}/${item.frontImg}`
            : ''
      ),
      brand: item.brandName || item.brand,
      brandName: item.brandName || item.brand,
      name: item.productName,
      hasVariants: !!(item.variants && item.variants.length && item.variants.length > 1),
      priceHtml,
    });

    element.firstElementChild.addEventListener('click', () => {
      EventEmitter.emit('sfListProductClick', item);
    });

    return element.firstElementChild;
  }

  update() {
    this.mainElement.innerHTML = '';
    if (this.search.results && this.search.results.length > 0) {
      let prevProduct = null;
      let currentRow = null;

      let allItems = (
        typeof this.params.modifyItemsBeforeRender === 'function'
          ? this.params.modifyItemsBeforeRender(this.search.results)
          : this.search.results
      );

      allItems.forEach((item, index) => {
        if (this.params.displayTopMatchesTitle && !prevProduct && item.score >= NEXT_BEST_MATCH_SCORE) {
          this.mainElement.insertAdjacentHTML(
            'beforeend',
            this.topMatchesTitleTemplate()
          );
        }
        if (
          item.product
          &&
          this.params.displayNextBestMatchesTitle
          &&
          (!prevProduct || prevProduct.score >= NEXT_BEST_MATCH_SCORE)
          &&
          item.score < NEXT_BEST_MATCH_SCORE
        ) {
          this.mainElement.insertAdjacentHTML(
            'beforeend',
            this.nextBestMatchingTitleTemplate({
              count: index,
            })
          );
        }

        if (this.params.row && index % this.params.row.perRow === 0) {
          currentRow = document.createElement('DIV');
          this.mainElement.appendChild(currentRow);
          if (this.params.row.className)
            currentRow.className = this.params.row.className;
        }

        const container = this.params.row ? currentRow : this.mainElement;

        const element = item.product ? this.renderItem(item.product) : this.renderCustomItem(item.params);

        container.appendChild(element);

        if (item.product) {
          prevProduct = item;
        }
      });
    }
    else {
      this.mainElement.innerHTML = this.nothingFoundTemplate();
    }

    window.requestAnimationFrame(() => {
      if (typeof this.params.onRendered === 'function') {
        this.params.onRendered(this.search);
      }
    });
  }

  render() {
    return this.mainElement;
  }
}

export default (params) => {
  return new ProductsList(params);
};
